import React, { useState } from 'react';
import { usePostComment } from '../hooks/usePostComment';
import { ArrowRight, Star } from 'lucide-react';
import type { Comment as CommentType } from '../api/comments';

interface AddCommentFormProps {
  bookId: number;
  onCancel: () => void;
  onSuccess?: () => void;
  parentComment?: CommentType; // if provided, we are replying to this comment
}

const AddCommentForm: React.FC<AddCommentFormProps> = ({ bookId, onCancel, onSuccess, parentComment }) => {
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const { mutate: postComment, isPending, isError } = usePostComment({
    onSuccess: () => {
      setComment('');
      onCancel();
      onSuccess?.();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) return;
    const isReply = !!parentComment;
    postComment({
      book_id: bookId,
      parent_review_id: isReply ? parentComment.review_id : null,
      comment,
      rating: isReply ? undefined : rating || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm mt-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-foreground font-persian">
          {parentComment
            ? (
              <>درحال پاسخ دادن به نظر <span className="font-bold">{parentComment.name || `کاربر ${parentComment.user_id}`}</span> هستید</>
            )
            : 'نظر خود را بنویسید'}
        </h3>
        <button onClick={onCancel} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
          <ArrowRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>
      {parentComment && (
        <div className="mb-3">
          <div className="text-sm text-muted-foreground font-persian bg-gray-50 border border-gray-200 rounded-md p-2">
            (( {parentComment.comment} ))
          </div>
        </div>
      )}
      {!parentComment && (
        <div className="flex items-center mb-4">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`w-6 h-6 cursor-pointer transition-colors ${
                (hoverRating || rating) >= star ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
              }`}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              onClick={() => setRating(star)}
            />
          ))}
        </div>
      )}
      <textarea
        className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow font-persian"
        rows={4}
        placeholder="نظر شما..."
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        disabled={isPending}
      />
      <button
        type="submit"
        className="w-full py-3 bg-[#7063ff] text-white font-bold rounded-lg transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed font-persian"
        disabled={comment.trim().length === 0 || (!parentComment && rating === 0) || isPending}
      >
        {isPending ? 'در حال ارسال...' : 'ارسال نظر'}
      </button>
      {isError && (
        <p className="text-red-500 text-sm mt-2 font-persian">خطا در ارسال نظر. لطفا دوباره تلاش کنید.</p>
      )}
    </form>
  );
};

export default AddCommentForm;
